import { type FC, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
// components
import { PriceDisplay } from 'dibs-buyer-price';
import { PdpRespTitle } from '../PdpRespTitle/PdpRespTitle.tsx';
import { PdpSharedPrivateOfferBanner } from '../PdpSharedPrivateOfferBanner/PdpSharedPrivateOfferBanner.tsx';
import { PdpSharedSidebarFavorites } from '../shared/PdpSharedSidebarFavorites/PdpSharedSidebarFavorites.tsx';
import { PdpSharedShippingBadgeLazy } from '../PdpSharedShippingBadge/PdpSharedShippingBadgeLazy.jsx';
import { PdpSharedFramingStatusLazy } from '../PdpSharedFramingStatus/PdpSharedFramingStatusLazy.jsx';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode.tsx';
import { PdpSharedCondensedSkuAttributes } from '../PdpSharedCondensedSkuAttributes/PdpSharedCondensedSkuAttributes.jsx';
import { PdpSharedCondensedSidebarButtons } from '../PdpSharedCondensedSidebarButtons/PdpSharedCondensedSidebarButtons.jsx';
import { PdpSharedBlurbLeadTime } from '../PdpSharedBlurbs/PdpSharedBlurbLeadTime.tsx';
import { BrowserHistoryContextProvider } from '../../global/BrowserHistoryContext/BrowserHistoryContext.tsx';
import { PdpRespSharedHeroCarousel } from '../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel.tsx';

// Helpers
import { getSelectedSku } from '../helpers/getSelectedSku.ts';
import { useAppSelector } from '../hooks/useReactReduxHooks.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

// styles
import styles from './PdpRespCondensedListedContent.scss';
import dibsCss from 'dibs-css';

// types
import { type PdpRespCondensedListedContent_viewer$key } from './__generated__/PdpRespCondensedListedContent_viewer.graphql.ts';
import { type PdpRespCondensedListedContent_user$key } from './__generated__/PdpRespCondensedListedContent_user.graphql.ts';
import { type PdpRespCondensedListedContent_item$key } from './__generated__/PdpRespCondensedListedContent_item.graphql.ts';

type Props = {
    currency: string;
    selectedSkuId?: string;
    viewer: PdpRespCondensedListedContent_viewer$key;
    user?: PdpRespCondensedListedContent_user$key;
    item: PdpRespCondensedListedContent_item$key;
};

export const PdpRespCondensedListedContent: FC<Props> = ({
    item: itemRef,
    user: userRef,
    viewer: viewerRef,
    currency,
    selectedSkuId,
}) => {
    const viewer = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_viewer on Viewer {
                showHeartCount: featureFlag(feature: "pdpDisplayHeartCount")
                ...PdpSharedShippingBadge_viewer
                ...PdpSharedPromoCode_viewer
                ...PdpRespSharedHeroCarousel_viewer
            }
        `,
        viewerRef
    );
    const user = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_user on User {
                ...PdpSharedCondensedSidebarButtons_user
                ...PdpSharedPromoCode_user
                ...PdpRespSharedHeroCarousel_user
                ...PdpSharedSidebarFavorites_user
            }
        `,
        userRef || null
    );
    const item = useFragment(
        graphql`
            fragment PdpRespCondensedListedContent_item on Item {
                firstPhotoWebPath(size: small)
                title
                displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                    ctas {
                        showAcceptOffer
                    }
                    ...PdpSharedPromoCode_displayInfo
                    ...PdpSharedCondensedSidebarButtons_displayInfo
                }
                seller {
                    isAccessible
                }
                skus {
                    serviceId
                    ...PdpSharedBlurbLeadTime_sku
                    ...PriceDisplay_sku
                        @include(if: $isMultiSku)
                        @arguments(
                            hasSkuId: true
                            userId: $userId
                            isTrade: $isTrade
                            userCountryCode: $userCountryCode
                            priceBookName: $priceBookName
                            catchNonFatalErrors: false
                        )
                }
                ...PriceDisplay_item
                    @include(if: $afterDisplayOrBtSsr)
                    @arguments(
                        userId: $userId
                        isTrade: $isTrade
                        userCountryCode: $userCountryCode
                        priceBookName: $priceBookName
                        catchNonFatalErrors: false
                    )
                ...PdpSharedCondensedSkuAttributes_item @include(if: $isMultiSku)
                ...PdpSharedShippingBadge_item
                ...PdpSharedFramingStatus_item @include(if: $isClient)
                ...PdpRespTitle_item
                ...PdpSharedCondensedSidebarButtons_item
                ...PdpSharedBlurbLeadTime_item
                ...PdpSharedSidebarFavorites_item
                ...PdpRespSharedHeroCarousel_item
            }
        `,
        itemRef
    );

    const [hasFreeShipping, setHasFreeShipping] = useState(false);
    const relayVariables = useAppSelector(state => state.relayVariables.variables);
    const { userId, itemId, isClient, isMultiSku, afterDisplayOrBtSsr } = relayVariables;
    const displayInfo = item?.displayInfo || null;
    const isSellerAccessible = item?.seller?.isAccessible;
    const selectedSku = getSelectedSku(item, selectedSkuId || '');
    const { isItemFavoritingFeb2026Variant } = useServerVarsContext();
    const showAcceptOffer = displayInfo?.ctas?.showAcceptOffer;

    return (
        <div
            className={classnames(styles.contentWrapper, {
                [styles.withBottomPadding]: !isSellerAccessible,
            })}
        >
            <div
                data-tn="condensed-image-carousel"
                className={classnames(styles.contentColumn, {
                    [dibsCss.relative]: isItemFavoritingFeb2026Variant,
                })}
            >
                {/* Image Carousel */}
                <BrowserHistoryContextProvider>
                    <PdpRespSharedHeroCarousel
                        viewer={viewer}
                        item={item}
                        user={user}
                        currency={currency}
                        afterDisplay={isClient}
                        size="xSmall"
                    />
                </BrowserHistoryContextProvider>
                {/* Favorite / Folder */}
                {isItemFavoritingFeb2026Variant && (
                    <div
                        className={classnames(
                            dibsCss.absolute,
                            dibsCss.topSmaller,
                            dibsCss.rightXxlarge,
                            dibsCss.prSmaller
                        )}
                    >
                        <PdpSharedSidebarFavorites
                            heartIconWrapperClass={styles.heartIconWrapper}
                            itemImage={item.firstPhotoWebPath || ''}
                            item={item}
                            user={user}
                            showHeartCount={viewer.showHeartCount}
                            isClient={!!isClient}
                            userId={userId}
                            wrapperClass={styles.favoritesWrapper}
                            animateHeartPulse={false}
                            showNudge
                            theme="circle-frosted-large"
                        />
                    </div>
                )}
            </div>

            {/* Sidebar */}
            <div className={styles.contentColumn}>
                <div
                    className={classnames(
                        !isItemFavoritingFeb2026Variant && styles.titleFavoritesWrapper
                    )}
                >
                    {/* Title */}
                    <PdpRespTitle item={item} />
                    {/* Favorite / Folder */}
                    {!isItemFavoritingFeb2026Variant && (
                        <PdpSharedSidebarFavorites
                            heartIconWrapperClass={styles.heartIconWrapper}
                            itemImage={item.firstPhotoWebPath || ''}
                            item={item}
                            user={user}
                            showHeartCount={viewer.showHeartCount}
                            isClient={!!isClient}
                            userId={userId}
                            wrapperClass={styles.favoritesWrapper}
                        />
                    )}
                </div>
                {isClient && showAcceptOffer && (
                    <PdpSharedPrivateOfferBanner className={styles.privateOfferContainer} />
                )}
                {/* Shipping Badge */}
                {isClient && (
                    <div
                        className={classnames(styles.shippingBadgeContainer, {
                            [styles.noMargin]: !hasFreeShipping,
                        })}
                    >
                        <PdpSharedShippingBadgeLazy
                            item={item}
                            viewer={viewer}
                            setHasFreeShipping={setHasFreeShipping}
                        />
                    </div>
                )}
                {/* Price */}
                <div className={styles.priceWithAccessoryInlineContainer}>
                    {afterDisplayOrBtSsr && (
                        <PriceDisplay
                            currency={currency}
                            item={item}
                            useSvgIcon
                            sku={isMultiSku ? selectedSku : null}
                            appendElementToPrice={<PdpSharedFramingStatusLazy item={item} />}
                            displayVatLabel
                            transaction={null}
                        />
                    )}
                </div>
                {/* Promo Code */}
                <PdpSharedPromoCode
                    displayInfo={displayInfo}
                    itemId={itemId}
                    user={user}
                    userId={userId}
                    viewer={viewer}
                    currency={currency}
                    excludeShippingPromos
                />

                {isMultiSku && (
                    <PdpSharedCondensedSkuAttributes item={item} selectedSkuId={selectedSkuId} />
                )}

                {/* Item Ctas */}
                <PdpSharedCondensedSidebarButtons
                    relayVariables={{
                        userId,
                        itemId,
                    }}
                    user={user}
                    item={item}
                    itemSearch={null}
                    displayInfo={displayInfo}
                    selectedSkuId={selectedSkuId}
                />

                <PdpSharedBlurbLeadTime sku={selectedSku} item={item} />
            </div>
        </div>
    );
};
